import { FC, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useMutation } from '@apollo/client';

import { COMPLETE_ACTIVITY_DAY, CREATE_USER_ACTIVITY, SET_ACTIVITY_REMINDER, UPDATE_USER_ACTIVITY } from '../../../state/mutations/UserActivity';
import { ReminderSettings } from '../../Challenges/ReminderFrame';
import { SessionContainerProps } from '../../Session/config';
import { composeActivityEntry } from '../../../utils/activities/composeActivityEntry';
import { registerNextInterceptor } from '../../../utils/session/nextInterceptor';
import {
  trackChallengeAccepted,
  trackChallengeDayCompleted,
  trackChallengeDeclined,
  trackChallengeProposed,
  trackChallengeReminderSet,
  trackChallengeStopped,
} from '../../../utils/analytics/challengeEvents';
import { useStores } from '../../../hooks/useStores';

export interface ActivityStepData extends Record<string, unknown> {
  activity?: any;
  /** the step's activity could not be fetched (e.g. removed server-side) */
  missing?: boolean;
  inPath?: boolean;
  acceptActivity: () => Promise<boolean>;
  /** "Maybe later" on the proposal (analytics only: nothing is stored) */
  declineActivity: () => void;
  saveReminder: (settings: ReminderSettings) => Promise<void>;
  /** leaves the running challenge (status stopped); true when done */
  removeActivity: () => Promise<boolean>;
  setDecided: (decided: boolean) => void;
}

/** "6:00 PM" (design picker) -> "18:00" (stored, user local time) */
const to24h = (label: string): string => {
  const match = label.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) {
    return label;
  }
  let hours = parseInt(match[1], 10) % 12;
  if (match[3].toUpperCase() === 'PM') {
    hours += 12;
  }
  return `${String(hours).padStart(2, '0')}:${match[2]}`;
};

const _ContainActivityStep: FC<SessionContainerProps<ActivityStepData>> = ({
  onNext,
  onFinish,
  children,
}) => {
  const rootStore = useStores();
  const contentId = rootStore.treeNav.content.sessionStep?.contentId;
  const pathId = rootStore.treeNav.content.session?.path;
  const sessionId = rootStore.treeNav.content.session?._id;
  const activity = contentId
    ? rootStore.activity.getActivity(contentId)
    : undefined;
  // a step restored from the persisted store can point at an activity that
  // no longer exists: fetch on demand, and when that fails let the step
  // step aside rather than block the session
  const missing = !!contentId && !activity && rootStore.activity.hasError(contentId);
  useEffect(() => {
    if (contentId && !activity) {
      rootStore.activity.fetchActivityIfNotExist(contentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);
  const inPath = !!pathId;
  const labels = rootStore?.language.translate.MOBILE?.CHALLENGE;

  // reaching this step shows the proposal, unless the challenge already runs
  const proposedRef = useRef(false);
  useEffect(() => {
    if (proposedRef.current || !activity?._id || activity.status === 'started') {
      return;
    }
    proposedRef.current = true;
    trackChallengeProposed(activity, pathId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?._id, activity?.status]);
  const declineActivity = useCallback(() => trackChallengeDeclined(activity, pathId), [activity, pathId]);

  const [createUserActivity] = useMutation(CREATE_USER_ACTIVITY);
  const [completeActivityDay] = useMutation(COMPLETE_ACTIVITY_DAY);
  const [setActivityReminder] = useMutation(SET_ACTIVITY_REMINDER);
  const [updateUserActivity] = useMutation(UPDATE_USER_ACTIVITY);

  const saveReminder = useCallback(async (settings: ReminderSettings) => {
    const userActivityId = activity?.userActivityId;
    if (!userActivityId) {
      return;
    }
    try {
      await setActivityReminder({
        variables: {
          userActivityId,
          reminderEnabled: settings.enabled,
          reminderTime: settings.enabled ? to24h(settings.time) : null,
        },
      });
      trackChallengeReminderSet(activity, settings.enabled, settings.enabled ? to24h(settings.time) : null, 'session');
    } catch (error) {
      console.error('Error saving the activity reminder:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.userActivityId, setActivityReminder]);

  const setDecided = (decided: boolean) =>
    rootStore.treeNav.choices.updateExercise({ decided });

  // the frames of the activity are grafted into the session tree just before
  // this step: their node keys are prefixed with this step's key
  const collectActivityQuestionIds = (): string[] => {
    const currentKey = rootStore.treeNav.current;
    const nodes = rootStore.treeNav.tree?.nodes || [];
    return nodes
      .filter((node: any) => node.key?.startsWith(`${currentKey}.`)
        && node.dataRef?.sessionStep?.contentType === 'QUESTION'
        && node.dataRef?.sessionStep?.contentId)
      .map((node: any) => node.dataRef.sessionStep.contentId);
  };

  // replaying a running challenge inside a path session: the frames the
  // user just went through count as today's occurrence (path sessions
  // never reach the activity-session end-of-session recording).
  // Guards: recordedRef makes this once-per-mount (acceptActivity sets it
  // too, since it records day 1 itself); the effect re-runs when the store
  // fills in late; a day already recorded today is never re-sent, so
  // re-entering the step cannot overwrite the entry.
  const recordedRef = useRef(false);
  // the entry sent with today's day: going back to the frames and editing
  // the answer, then continuing, re-sends the day (same day = update)
  const sentEntryRef = useRef<string | null>(null);
  useEffect(() => registerNextInterceptor(() => {
    const userActivityId = activity?.userActivityId;
    if (userActivityId && activity?.status === 'started' && rootStore.activity.isDoneToday(activity._id)) {
      const entry = composeActivityEntry(rootStore, sessionId as string, collectActivityQuestionIds());
      if (entry && entry !== sentEntryRef.current) {
        sentEntryRef.current = entry;
        completeActivityDay({ variables: { userActivityId, entry } })
          .then(({ data }) => {
            if (data?.completeActivityDay) {
              rootStore.activity.setUserActivity(activity._id, userActivityId, data.completeActivityDay.status, true, data.completeActivityDay.days);
              trackChallengeDayCompleted(activity, data.completeActivityDay, 'session');
            }
          })
          .catch(error => console.error('Error updating the challenge day entry:', error));
      }
    }
    // never consumes the press: the session moves on
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [activity?.userActivityId, activity?.status, activity?._id, sessionId]);
  useEffect(() => {
    const userActivityId = activity?.userActivityId;
    if (recordedRef.current || !userActivityId || activity?.status !== 'started') {
      return;
    }
    if (rootStore.activity.isDoneToday(activity._id)) {
      return;
    }
    recordedRef.current = true;
    (async () => {
      try {
        const entry = composeActivityEntry(rootStore, sessionId as string, collectActivityQuestionIds());
        sentEntryRef.current = entry || null;
        const { data } = await completeActivityDay({
          variables: { userActivityId, entry: entry || null },
        });
        if (data?.completeActivityDay) {
          rootStore.activity.setUserActivity(
            activity._id,
            userActivityId,
            data.completeActivityDay.status,
            true,
            data.completeActivityDay.days,
          );
          trackChallengeDayCompleted(activity, data.completeActivityDay, 'session');
        }
      } catch (error) {
        console.error('Error completing the in-path challenge day:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.userActivityId, activity?.status]);

  const removeActivity = useCallback(async () => {
    const userActivityId = activity?.userActivityId;
    if (!userActivityId) {
      return false;
    }
    try {
      const daysBefore = activity?.days?.slice() || [];
      await updateUserActivity({ variables: { input: { _id: userActivityId, status: 'stopped' } } });
      rootStore.activity.setUserActivity(activity._id, userActivityId, 'stopped', false, []);
      trackChallengeStopped(activity, 'session', daysBefore);
      // a later acceptance starts a fresh challenge and records its day 1
      recordedRef.current = false;
      sentEntryRef.current = null;
      return true;
    } catch (error) {
      console.error('Error removing the challenge:', error);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.userActivityId, activity?._id, updateUserActivity]);

  const acceptActivity = useCallback(async () => {
    if (!activity?._id) {
      console.error('Missing activity for user activity creation');
      return false;
    }
    // day 1 is recorded right below — the replay effect must not also fire
    // when the store turns to 'started'
    recordedRef.current = true;
    try {
      const { data } = await createUserActivity({
        variables: {
          input: {
            activity: activity._id,
            isChallenge: true,
            fromPath: pathId,
            fromSession: sessionId,
            language: activity.language,
          },
        }
      });
      const userActivityId = data?.createUserActivity?._id;
      if (!userActivityId) {
        recordedRef.current = false;
        return false;
      }
      rootStore.activity.setUserActivity(activity._id, userActivityId, 'started', true, data?.createUserActivity?.days);
      trackChallengeAccepted(activity, 'session', pathId);
      // the user just did the activity within the session: that is day 1
      try {
        const entry = composeActivityEntry(rootStore, sessionId as string, collectActivityQuestionIds());
        sentEntryRef.current = entry || null;
        const { data: dayData } = await completeActivityDay({
          variables: { userActivityId, entry: entry || null },
        });
        if (dayData?.completeActivityDay) {
          rootStore.activity.setUserActivity(
            activity._id,
            userActivityId,
            dayData.completeActivityDay.status,
            true,
            dayData.completeActivityDay.days,
          );
          trackChallengeDayCompleted(activity, dayData.completeActivityDay, 'session');
        }
      } catch (dayError) {
        console.error('Error completing first activity day:', dayError);
      }
      return true;
    } catch (error: any) {
      recordedRef.current = false;
      const errorText = JSON.stringify(error?.graphQLErrors || error?.message || error || '');
      if (errorText.includes('ALREADY_EXISTING') || errorText.includes('already started')) {
        // started elsewhere (e.g. from the library): treat as accepted — sync
        // the real user activity and let the replay effect record today
        await rootStore.activity.fetchMyActivity();
        trackChallengeAccepted(activity, 'session', pathId);
        return true;
      }
      console.error('Error creating user activity:', error);
      return false;
    }
  }, [activity?._id, activity?.language, pathId, sessionId, createUserActivity, completeActivityDay, rootStore]);

  return children({
    onNext,
    onFinish,
    data: {
      activity,
      missing,
      inPath,
      acceptActivity,
      declineActivity,
      saveReminder,
      removeActivity,
      setDecided,
    },
    labels,
  });
};

export const ContainActivityStep = observer(_ContainActivityStep);
